import { Centrifuge, PublicationContext, Subscription } from 'centrifuge';
import { errorCodes } from 'centrifuge/build/codes';
import { resolveService } from 'SharedDir/utils/vue';
import { InjectionKey } from 'vue';
import { ServerJoinContext, ServerLeaveContext, SubscribedContext } from 'centrifuge/build/types';

export const CentrifugeKey = <InjectionKey<() => Centrifuge | undefined>>(
  Symbol('Centrifuge injection key')
);

export default () => {
  const factory = resolveService(CentrifugeKey, () => undefined, true);
  if (!factory) {
    throw new Error('Centrifuge not set!');
  }
  const centrifuge = factory() as Centrifuge;
  /**
   * Подписываемся на событие публикации в определенном канале (событие)
   *
   * @param {string} name
   * @param {(ctx: PublicationContext) => void} callback
   * @returns {Promise<void>}
   */
  async function subscribePublication(name: string, callback: (ctx: PublicationContext) => void) {
    await centrifugeReady();
    const subscription = centrifugeSubscribe(name);
    subscription.on('publication', callback);
  }

  async function subscribePresence(name: string) {
    await centrifugeReady();
    const subscription = centrifugeSubscribe(name);
    return subscription.presence();
  }

  async function subscribePresenceJoin(name: string, callback: (ctx: ServerJoinContext) => void) {
    await centrifugeReady();
    const subscription = centrifugeSubscribe(name);
    subscription.on('join', callback);
  }

  async function subscribePresenceLeave(name: string, callback: (ctx: ServerLeaveContext) => void) {
    await centrifugeReady();
    const subscription = centrifugeSubscribe(name);
    subscription.on('leave', callback);
  }

  /**
   * Поиск подписок по регулярке
   * @param regex
   */
  // const regex = /report_presence.*/g;
  async function subscriptionsPresenceList(regex) {
    await centrifugeReady();
    const subscription = centrifuge.subscriptions();
    const result = Object.keys(subscription).filter((channel) => channel.match(regex));
    return result;
  }

  /**
   * Проверка на активность подключения к центрифуге
   * @returns {Promise<void>}
   * @private
   */
  async function centrifugeReady(): Promise<void> {
    try {
      await centrifuge.ready();
    } catch (e: any) {
      if (e.code !== errorCodes.clientDisconnected) {
        throw e;
      }
      centrifuge.connect();
    }
  }

  /**
   * Оформляем подписку на определенный канал (событие)
   *
   * @param {string} name
   * @returns {Subscription}
   * @private
   */
  function centrifugeSubscribe(name: string): Subscription {
    const existing = centrifuge.getSubscription(name);
    if (existing) {
      console.warn(`Subscription for ${name} already exists`);
      return existing;
    }

    const subscription = centrifuge.newSubscription(name);
    subscription.subscribe();
    return subscription;
  }

  /**
   * Отменяем подписки на канал/каналы и отключаемая от центрифуги, если активных подписок нет
   *
   * @private
   */
  function centrifugeShutdown(subscriptionName?: string | Array<string>): void {
    if (subscriptionName) {
      if (Array.isArray(subscriptionName)) {
        subscriptionName.forEach(centrifugeUnsubscribe);
      } else {
        centrifugeUnsubscribe(subscriptionName);
      }
    } else {
      Object.values(centrifuge.subscriptions()).forEach(centrifugeUnsubscribe);
    }

    // const countActiveSubscriptions = Object.values(centrifuge.subscriptions()).length;
    // if (countActiveSubscriptions === 0) {
    // centrifuge.disconnect();
    // }
  }

  /**
   * Отменяем подписку на канал (событие)
   *
   * @param {string | Subscription} name
   * @private
   */
  function centrifugeUnsubscribe(name: string | Subscription): void {
    let subscription: Subscription | null;
    if (typeof name === 'string') {
      subscription = centrifuge.getSubscription(name);
    } else {
      subscription = name;
    }
    if (!subscription) {
      console.warn(`Subscription ${name} doesn't exist!`);
      return;
    }
    subscription.unsubscribe();
    subscription.removeAllListeners();
    centrifuge.removeSubscription(subscription);
  }

  return {
    centrifuge,
    subscribePublication,
    centrifugeReady,
    centrifugeSubscribe,
    centrifugeShutdown,
    centrifugeUnsubscribe,
    subscribePresence,
    subscribePresenceJoin,
    subscribePresenceLeave,
    subscriptionsPresenceList,
  };
};
